import * as THREE from 'three';
import type { PixelPosition } from '../../tilemap/types';
import {
  CAMERA_LOOK_SMOOTHING,
  CAMERA_ORBIT_STEP,
  CAMERA_SETTLE_EPSILON,
  CAMERA_TILT_STEP,
  CAMERA_ZOOM_STEP,
  DEFAULT_CAMERA_AZIMUTH,
  DEFAULT_CAMERA_DISTANCE,
  DEFAULT_CAMERA_TILT,
  MAX_CAMERA_DISTANCE,
  MAX_CAMERA_TILT,
  MIN_CAMERA_DISTANCE,
  MIN_CAMERA_TILT,
  type OrbitDirection,
  type ProjectionController,
} from './ProjectionController';

export class PerspectiveProjectionController implements ProjectionController {
  public readonly camera = new THREE.PerspectiveCamera(52, 1, 0.1, 300);

  private readonly cameraTarget = new THREE.Vector3();
  private readonly desiredCameraTarget = new THREE.Vector3();
  private cameraDistance = DEFAULT_CAMERA_DISTANCE;
  private cameraAzimuth = DEFAULT_CAMERA_AZIMUTH;
  private cameraTilt = DEFAULT_CAMERA_TILT;

  public syncViewport(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  public updateCamera(target: THREE.Vector3): boolean {
    this.desiredCameraTarget.copy(target);
    let animating = false;
    if (this.cameraTarget.lengthSq() === 0) {
      this.cameraTarget.copy(this.desiredCameraTarget);
    } else {
      animating = this.cameraTarget.distanceToSquared(this.desiredCameraTarget) > CAMERA_SETTLE_EPSILON * CAMERA_SETTLE_EPSILON;
      this.cameraTarget.lerp(this.desiredCameraTarget, CAMERA_LOOK_SMOOTHING);
      if (!animating && this.cameraTarget.distanceToSquared(this.desiredCameraTarget) > 0) {
        this.cameraTarget.copy(this.desiredCameraTarget);
      }
    }

    const horizontalDistance = Math.cos(this.cameraTilt) * this.cameraDistance;
    const cameraOffset = new THREE.Vector3(
      Math.cos(this.cameraAzimuth) * horizontalDistance,
      Math.sin(this.cameraTilt) * this.cameraDistance,
      Math.sin(this.cameraAzimuth) * horizontalDistance,
    );
    this.camera.position.copy(this.cameraTarget).add(cameraOffset);
    this.camera.lookAt(this.cameraTarget);
    return animating;
  }

  public snapToTarget(target: THREE.Vector3): void {
    this.desiredCameraTarget.copy(target);
    this.cameraTarget.copy(target);
    const horizontalDistance = Math.cos(this.cameraTilt) * this.cameraDistance;
    const cameraOffset = new THREE.Vector3(
      Math.cos(this.cameraAzimuth) * horizontalDistance,
      Math.sin(this.cameraTilt) * this.cameraDistance,
      Math.sin(this.cameraAzimuth) * horizontalDistance,
    );
    this.camera.position.copy(this.cameraTarget).add(cameraOffset);
    this.camera.lookAt(this.cameraTarget);
  }

  public rotateOrbit(direction: OrbitDirection): void {
    const delta = direction === 'left' ? -CAMERA_ORBIT_STEP : CAMERA_ORBIT_STEP;
    this.cameraAzimuth = THREE.MathUtils.euclideanModulo(this.cameraAzimuth + delta, Math.PI * 2);
  }

  public adjustZoom(deltaY: number): boolean {
    const nextDistance = THREE.MathUtils.clamp(
      this.cameraDistance * (1 + deltaY * CAMERA_ZOOM_STEP),
      MIN_CAMERA_DISTANCE,
      MAX_CAMERA_DISTANCE,
    );
    if (Math.abs(nextDistance - this.cameraDistance) < 1e-5) {
      return false;
    }
    this.cameraDistance = nextDistance;
    return true;
  }

  public adjustTilt(deltaY: number): boolean {
    const nextTilt = THREE.MathUtils.clamp(
      this.cameraTilt - deltaY * CAMERA_TILT_STEP,
      MIN_CAMERA_TILT,
      MAX_CAMERA_TILT,
    );
    if (Math.abs(nextTilt - this.cameraTilt) < 1e-5) {
      return false;
    }
    this.cameraTilt = nextTilt;
    return true;
  }

  public setRayFromScreen(
    screenX: number,
    screenY: number,
    canvasWidth: number,
    canvasHeight: number,
    raycaster: THREE.Raycaster,
    ndcPointer: THREE.Vector2,
  ): void {
    ndcPointer.set(
      (screenX / canvasWidth) * 2 - 1,
      -(screenY / canvasHeight) * 2 + 1,
    );
    raycaster.setFromCamera(ndcPointer, this.camera);
  }

  public projectWorldPoint(
    worldX: number,
    worldY: number,
    worldZ: number,
    canvasWidth: number,
    canvasHeight: number,
    scratch: THREE.Vector3,
  ): PixelPosition {
    scratch.set(worldX, worldY, worldZ).project(this.camera);
    return {
      x: (scratch.x * 0.5 + 0.5) * canvasWidth,
      y: (-scratch.y * 0.5 + 0.5) * canvasHeight,
    };
  }
}
